import * as React from 'react';
import * as Autocomplete from 'react-autocomplete';
import { Link, withRouter } from 'react-router-dom';
import { Color, ResponsiveSize } from 'src/components/Globals/Globals';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { Button } from '../Button/Button';
import { AppConsumer } from 'src/contexts/Context';
import { Solution } from 'src/models/Solution';
import FuzzySearch from 'fuzzy-search';

const StyledAutocompleteInput = styled.div`
  width: 100%;
  flex-grow: 1;

  @media (max-width: ${ResponsiveSize.tablet}) {
    display: flex;
    & > div {
      flex-grow: 1;
    }

    $ > div > form {
      display: flex;
      width: 100%;
    }

    form {
      display: flex;
      width: 100%;
    }
  }

  @media (min-width: ${ResponsiveSize.laptop}) {
    input {
      width: 300px !important;
    }
  }

  @media (min-width: ${ResponsiveSize.laptopL}) {
    input {
      width: 450px !important;
    }
  }

  @media (min-width: ${ResponsiveSize.desktop}) {
    input {
      width: 600px !important;
    }
  }

  div {
    z-index: 999;
    /* Shows 7 elements */
    max-height: 216px !important;
    @media (max-width: ${ResponsiveSize.tablet}) {
      left: 0 !important;
      right: 0 !important;
      margin-left: 1em;
      flex: 1;
    }
  }

  input {
    font-size: 14px;
    padding: 10px 20px;
    width: 100%;
    box-sizing: border-box;
    border: none;
  }
`;

const StyledAutocompleteItem = styled.div<{ highlighted?: boolean }>`
  font-size: 14px;
  padding: 0.5em 1em;
  background-color: ${props => (props.highlighted ? Color.lighterBlue : Color.white)};
`;

const StyledAutocompleteSearchButton = styled(Button)`
  background: none;
  border: none;
  vertical-align: top;
  color: black;
  font-size: 14px;
  color: ${Color.black};
`;

const ClearButton = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${Color.white};
  font-size: 18px;
  cursor: pointer;
  padding: 0 10px;

  &:hover {
    color: ${Color.red};
  }
`;

interface IAutoCompleteInputState {
  selectedValue: string;
  shownResults: Solution[];
  autoCorrectMenuOpen: boolean;
}

class AutocompleteInputComponent extends React.Component<any, IAutoCompleteInputState> {
  constructor(props: any) {
    super(props);
    this.state = { selectedValue: '', shownResults: [], autoCorrectMenuOpen: false };
  }

  /** Is called whenever changes are made to the autocomplete input field */
  public onChange(solutions: Solution[]) {
    const fuzzySearch = new FuzzySearch(solutions, ['solutionName'], {
      caseSensitive: false,
      sort: true
    });
    const results: Solution[] = fuzzySearch.search(this.state.selectedValue);
    this.setState({ shownResults: results.slice(0, 20) });
  }

  public render() {
    const { selectedValue } = this.state;

    return (
      <AppConsumer>
        {({ state, actions }) => (
          <StyledAutocompleteInput>
            <form
              onSubmit={e => {
                e.preventDefault();
                actions.filterSolutionsByText(selectedValue);
                // If we are not in the front page, enter front page to see results.
                this.props.history.push('/');
              }}
              style={{ display: 'flex' }}
            >
              <Autocomplete
                items={this.state.shownResults}
                // Should item render -function is not used. shownResults -variable is updated manually with fuzzySearch
                shouldItemRender={(item, value) => true}
                getItemValue={item => item.solutionName}
                renderItem={(item: Solution, highlighted) => {
                  const itemName = item.solutionName
                    .toUpperCase()
                    .replace(selectedValue.toUpperCase(), `<b>${selectedValue.toUpperCase()}</b>`);
                  return (
                    <StyledAutocompleteItem key={item.id} highlighted={highlighted}>
                      <Link
                        to={`/offering/${item.id}`}
                        dangerouslySetInnerHTML={{ __html: itemName }}
                      />
                    </StyledAutocompleteItem>
                  );
                }}
                value={selectedValue}
                // Triggered when the user types something
                onChange={e =>
                  this.setState({ selectedValue: e.target.value }, () => {
                    // Filter autocomplete results
                    this.onChange(state.solutions);
                    // Reset all filters except text search
                    actions.resetFilters();
                    // Filter solutions and display them on Search page
                    actions.filterSolutionsByText(this.state.selectedValue);
                  })
                }
                // Triggers when a result is selected form autocorrect list
                onSelect={value =>
                  this.setState({ selectedValue: value }, () => {
                    actions.resetFilters();
                    actions.filterSolutionsByText(this.state.selectedValue);
                  })
                }
                inputProps={{ placeholder: 'Search for an offering...' }}
                wrapperStyle={{ display: 'inline-block' }}
                autoHighlight={false}
                onMenuVisibilityChange={isOpen => this.setState({ autoCorrectMenuOpen: isOpen })}
                open={this.state.autoCorrectMenuOpen && this.state.selectedValue.length > 0}
              />
              <ClearButton
                data-tip="Clear text"
                onClick={() => {
                  this.setState({ selectedValue: '' });
                  actions.clearTextSearch().then(() => {
                    actions.filterSolutions();
                  });
                }}
              >
                ×
              </ClearButton>

              <StyledAutocompleteSearchButton type="submit">
                <FontAwesomeIcon icon="search" />
              </StyledAutocompleteSearchButton>
            </form>
          </StyledAutocompleteInput>
        )}
      </AppConsumer>
    );
  }
}

const AutocompleteInput = withRouter(AutocompleteInputComponent);
export { AutocompleteInput };



// WEBPACK FOOTER //
// ./src/components/AutocompleteInput/AutocompleteInput.tsx